import { VStack } from '@chakra-ui/react';
import mixpanel from 'mixpanel-browser';
import { useState } from 'react';

import { AiSearchTrackingEvents } from '@revelio/core';

import { useTabContentConfig } from '../../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../../components/PageConfigProvider/types';
import { useFilterContext } from '../../contexts/FilterProvider';
import type { MultiInstanceMeta } from '../../hooks/useMultiFilterEditor';
import {
  type Filter,
  FilterId,
  FilterType,
  type FreeTextFilter,
  type TreeFilter,
  type TreeSelection,
} from '../../types/filters';
import { isDiscoveryPage, isTreeFilter } from '../../utils/common';
import DateFilterForm from '../filterForms/DateFilterForm';
import { DateRangeFilterForm } from '../filterForms/DateRangeFilterForm';
import { FreeTextFilterForm } from '../filterForms/FreeTextFilterForm';
import { ListFilterForm } from '../filterForms/ListFilterForm';
import { NotToggle } from '../filterForms/NotToggle';
import { RangeFilterForm } from '../filterForms/RangeFilterForm';
import { TreeFilterForm } from '../filterForms/TreeFilterForm';
import { AiSearchFilterForm } from '../filterForms/talent-discovery/ai-search/AiSearchFilterForm';
import { NameFilterForm } from '../filterForms/talent-discovery/NameFilterForm';
import { SchoolSearchFilterForm } from '../filterForms/talent-discovery/SchoolSearchFilterForm';
import { StaticListFilterForm } from '../filterForms/talent-discovery/StaticListFilterForm';

type FilterFormRenderProps = {
  filter: Filter | null;
  close: () => void;
  selectedMultiMeta?: MultiInstanceMeta;
  selectedFilterId?: FilterId | 'AI_SEARCH' | null;
  groupKeyOverride?: string;
  disabledLevels?: number[];
  selectionLimit?: number;
  showSelectedValues?: boolean;
  currentTreeSelections?: TreeSelection[];
  replaceSingleSelections?: boolean;
  allowMultiLevelSelect?: boolean;
  allowedPresetValues?: string[];
};

export const FilterFormRender = ({
  filter,
  close,
  selectedMultiMeta,
  selectedFilterId,
  groupKeyOverride,
  disabledLevels,
  selectionLimit,
  showSelectedValues,
  currentTreeSelections,
  replaceSingleSelections = false,
  allowMultiLevelSelect,
  allowedPresetValues,
}: FilterFormRenderProps) => {
  const { dispatch } = useFilterContext();
  const { revelioPageId } = useTabContentConfig();

  // Centralized isNot state management for filters that support it
  const supportsIsNot =
    filter &&
    (filter.type === FilterType.LIST ||
      filter.type === FilterType.STATIC_LIST ||
      filter.type === FilterType.TREE ||
      filter.type === FilterType.RANGE ||
      filter.type === FilterType.FREE_TEXT ||
      filter.type === FilterType.SCHOOL);

  // showNotToggle: whether to display the NotToggle UI component
  // Only show when on Talent Discovery page
  const showNotToggle: boolean =
    (revelioPageId === RevelioPageId.TALENT_DISCOVERY ||
      revelioPageId === RevelioPageId.POSTING_DISCOVERY) &&
    !!supportsIsNot;

  // isNotEnabled: whether the "not" logic is currently enabled for this filter
  const [isNotEnabled, setIsNotEnabled] = useState<boolean>(
    filter && 'isNot' in filter ? (filter.isNot ?? false) : false
  );

  const treeFilterForForm: TreeFilter | null =
    filter && isTreeFilter(filter) ? filter : null;

  const freeTextFilterForForm: FreeTextFilter | null =
    filter?.type === FilterType.FREE_TEXT && filter.id !== FilterId.NAME
      ? (filter as FreeTextFilter)
      : null;

  const handleSaveFilter = (updatedFilter: Filter) => {
    const isNotValue = (() => {
      if (updatedFilter.type === FilterType.RANGE) {
        return 'isNot' in updatedFilter
          ? (updatedFilter.isNot ?? false)
          : false;
      }

      if (!supportsIsNot) {
        return undefined;
      }

      if ('isNot' in updatedFilter && updatedFilter.isNot !== undefined) {
        return updatedFilter.isNot;
      }

      return isNotEnabled;
    })();

    const nextFilter: Filter = {
      ...updatedFilter,
      isUserSubmitted: true,
      ...(isNotValue !== undefined && { isNot: isNotValue }),
    };

    const selectionCount =
      'selections' in nextFilter && Array.isArray(nextFilter.selections)
        ? nextFilter.selections.length
        : undefined;
    mixpanel.track(AiSearchTrackingEvents.FILTERS_APPLIED, {
      method: 'manual',
      filter_id: nextFilter.id,
      filter_type: nextFilter.type,
      is_multi_instance: Boolean(selectedMultiMeta),
      ...(isNotValue !== undefined && { is_not: isNotValue }),
      ...(selectionCount !== undefined && { selection_count: selectionCount }),
      page_id: revelioPageId,
    });

    if (selectedMultiMeta) {
      if (selectedMultiMeta.isNewMultiInstance) {
        dispatch({
          type: 'ADD_MULTI_FILTER_INSTANCE',
          payload: {
            parentId: selectedMultiMeta.parentMultiFilterId,
            newInstance: nextFilter,
          },
        });
      } else if (selectedMultiMeta.instanceIndex !== undefined) {
        dispatch({
          type: 'UPDATE_MULTI_FILTER_INSTANCE',
          payload: {
            parentId: selectedMultiMeta.parentMultiFilterId,
            instanceIndex: selectedMultiMeta.instanceIndex,
            newInstance: nextFilter,
          },
        });
      }
      return;
    }

    dispatch({ type: 'SET_FILTER', filter: nextFilter, groupKeyOverride });
  };

  // Helper to wrap form with NotToggle if needed (except forms that handle it internally)
  const renderFormWithNotToggle = (
    form: React.ReactNode,
    filterType: FilterType
  ) => {
    // ListFilterForm renders NotToggle inside CustomMenuList, so don't wrap it
    if (filterType === FilterType.LIST) {
      return form;
    }

    // TreeFilterForm handles NotToggle internally via middleContent for Company filter
    // and other tree filters, so don't wrap it when showNotToggle is passed
    if (filterType === FilterType.TREE && showNotToggle) {
      return form;
    }

    // FreeTextFilterForm handles NotToggle inline within the "Contains any of/none of" text
    if (filterType === FilterType.FREE_TEXT && showNotToggle) {
      return form;
    }

    if (showNotToggle && filter) {
      return (
        <VStack spacing={2} align="stretch">
          <NotToggle
            filterName={filter.name}
            isNot={isNotEnabled}
            onChange={setIsNotEnabled}
            pl={0}
          />
          {form}
        </VStack>
      );
    }

    return form;
  };

  return (
    <>
      {filter?.id === FilterId.SNAPSHOT_DATE && (
        <DateFilterForm closeMenu={close} />
      )}
      {filter?.id === FilterId.DATE_RANGE && (
        <DateRangeFilterForm
          closeMenu={close}
          allowedPresetValues={allowedPresetValues}
        />
      )}
      {filter?.id === FilterId.POST_DATE && (
        <DateRangeFilterForm
          closeMenu={close}
          allowedPresetValues={allowedPresetValues}
          filterId={FilterId.POST_DATE}
          granularity="week"
        />
      )}
      {filter?.type === FilterType.SCHOOL &&
        renderFormWithNotToggle(
          <SchoolSearchFilterForm
            filter={filter}
            onClose={close}
            onSave={handleSaveFilter}
            isNewInstance={selectedMultiMeta?.isNewMultiInstance}
          />,
          FilterType.SCHOOL
        )}
      {treeFilterForForm &&
        renderFormWithNotToggle(
          <TreeFilterForm
            filter={treeFilterForForm}
            disallowMultiLevelSelect={
              allowMultiLevelSelect ? false : !isDiscoveryPage(revelioPageId)
            }
            closeMenu={close}
            onSave={handleSaveFilter}
            isNewInstance={selectedMultiMeta?.isNewMultiInstance}
            isNotEnabled={isNotEnabled}
            onIsNotEnabledChange={setIsNotEnabled}
            showNotToggle={showNotToggle}
            disabledLevels={disabledLevels}
            maxSelections={selectionLimit}
            showSelectedValues={showSelectedValues}
            currentTreeSelections={currentTreeSelections}
            replaceSingleSelections={replaceSingleSelections}
          />,
          FilterType.TREE
        )}
      {/* Instance manager removed: selecting a multi filter from the menu auto-opens a new instance; editing from a tag updates directly */}
      {filter?.type === FilterType.LIST && (
        <ListFilterForm
          filter={filter}
          closeMenu={close}
          onSave={handleSaveFilter}
          isNotEnabled={isNotEnabled}
          onIsNotEnabledChange={setIsNotEnabled}
          showNotToggle={showNotToggle}
        />
      )}
      {filter?.type === FilterType.STATIC_LIST && (
        <StaticListFilterForm
          filter={filter}
          closeMenu={close}
          onSave={handleSaveFilter}
          isNotEnabled={isNotEnabled}
          onIsNotEnabledChange={setIsNotEnabled}
          showNotToggle={showNotToggle}
        />
      )}
      {filter?.type === FilterType.FREE_TEXT &&
        filter?.id === FilterId.NAME && (
          <NameFilterForm filter={filter} closeMenu={close} />
        )}
      {freeTextFilterForForm && (
        <FreeTextFilterForm
          filter={freeTextFilterForForm}
          closeMenu={close}
          onSave={handleSaveFilter}
          isNewInstance={selectedMultiMeta?.isNewMultiInstance}
          isNotEnabled={isNotEnabled}
          onIsNotEnabledChange={setIsNotEnabled}
          showNotToggle={showNotToggle}
        />
      )}
      {filter?.type === FilterType.RANGE && (
        <RangeFilterForm
          filter={filter}
          closeMenu={close}
          onSave={handleSaveFilter}
          showNotToggle={showNotToggle}
        />
      )}
      {selectedFilterId === 'AI_SEARCH' && (
        <AiSearchFilterForm closeMenu={close} />
      )}
    </>
  );
};
